import type { ReconciliationIssue, ReconciliationRun } from "@getaways/tours";
import { api, Banner, dateTime, Empty, go, PageHead, useAsync, usePrompt, useToast, type Notice } from "@getaways/extranet-ui";
import { useState } from "react";
import { useCan } from "../lib/access";

const KIND: Record<ReconciliationIssue["kind"], string> = {
  missing_at_supplier: "Not found at the supplier",
  status_mismatch: "Different status",
  price_mismatch: "Different price",
  commission_mismatch: "Different commission",
};

/** Nightly comparison with the supplier; differences wait here until explained or fixed. */
export function ReconciliationPage() {
  const can = useCan();
  const { data, error, reload } = useAsync(() => api<{ runs: ReconciliationRun[]; issues: ReconciliationIssue[] }>("GET", "/v1/admin/reconciliation"), []);
  const [notice, setNotice] = useState<Notice>();
  const toast = useToast();
  const prompt = usePrompt();
  const run = async () => {
    const r = await api<ReconciliationRun>("POST", "/v1/admin/reconciliation/run");
    setNotice(undefined);
    toast(`${r.checked} bookings checked, ${r.issues} differences.`);
    reload();
  };
  const settle = async (i: ReconciliationIssue, status: "explained" | "fixed") => {
    const note = await prompt({ title: status === "explained" ? "Why is this right?" : "What was fixed?", label: "Note (optional)", multiline: true, confirmLabel: status === "explained" ? "Mark explained" : "Mark fixed" });
    // Cancelling now leaves the difference open (window.prompt's Cancel used to settle it without a note).
    if (note === null) return;
    void api("PUT", `/v1/admin/reconciliation/issues/${i.id}`, { status, ...(note ? { note } : {}) }).then(reload);
  };

  return (
    <>
      <PageHead title="Reconciliation" sub="Our bookings, prices and commissions compared with the supplier's every night.">
        {can("reconciliation:manage") && <button onClick={() => void run()}>Run now</button>}
      </PageHead>
      {(error || notice) && <Banner notice={notice ?? { tone: "bad", text: error! }} onClose={() => setNotice(undefined)} />}
      <div className="card" style={{ padding: 0 }}>
        <h2 style={{ padding: "12px 14px 0" }}>Open differences</h2>
        {data && data.issues.length === 0 ? <Empty>None.</Empty> : (
          <table className="table">
            <thead><tr><th>Difference</th><th>Ours</th><th>Supplier</th><th>Found</th><th /></tr></thead>
            <tbody>
              {(data?.issues ?? []).map((i) => (
                <tr key={i.id} className={i.bookingId ? "click" : ""} onClick={() => i.bookingId && go("bookings", i.bookingId)}>
                  <td>{KIND[i.kind]}<div className="small muted mono">{i.supplierReference}</div></td>
                  <td className="small">{i.ours ?? "—"}</td>
                  <td className="small">{i.theirs ?? "—"}</td>
                  <td className="small muted">{dateTime(i.createdAt)}</td>
                  <td onClick={(e) => e.stopPropagation()}>{can("reconciliation:manage") && <><button onClick={() => void settle(i, "explained")}>Explained</button><button onClick={() => void settle(i, "fixed")}>Fixed</button></>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="card">
        <h2>Runs</h2>
        {(data?.runs ?? []).length === 0 ? <Empty>No runs yet.</Empty> : (data?.runs ?? []).map((r) => <div key={r.id} className="small">{dateTime(r.startedAt)} · {r.supplier} · {r.checked} checked · {r.issues} differences</div>)}
      </div>
    </>
  );
}
